import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis
} from "recharts";
import { 
  CurrencyDollar, 
  Warning, 
  CalendarCheck, 
  Clock,
  ArrowRight,
  Plus,
  ChartPie,
  TrendUp,
  Wallet,
  Eye,
  EyeSlash
} from "@phosphor-icons/react";

const CATEGORY_COLORS = [
  "#E07A5F", "#3D405B", "#81B29A", "#F2CC8F", "#9A8C98", "#C9ADA7", "#264653", "#E9C46A"
];

const MONTHS = [
  { value: "1", label: "Janeiro" },
  { value: "2", label: "Fevereiro" },
  { value: "3", label: "Março" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Maio" },
  { value: "6", label: "Junho" },
  { value: "7", label: "Julho" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" }
];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [bills, setBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [showPending, setShowPending] = useState(true);
  const [showPaid, setShowPaid] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    autoBackup();
  }, []);

  const autoBackup = async () => {
    try {
      // Only backup once per day
      const lastBackup = localStorage.getItem("last_auto_backup");
      const today = new Date().toISOString().split("T")[0];
      if (lastBackup === today) return;

      const response = await api.get("/backup/export");
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup_financeiro_${today}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      localStorage.setItem("last_auto_backup", today);
    } catch (error) {
      console.error("Auto backup failed:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, billsRes, catsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/bills"),
        api.get("/categories")
      ]);
      setStats(statsRes.data);
      setBills(billsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate pending bills by category for selected month
  const getPendingByCategory = () => {
    const month = parseInt(selectedMonth);
    const year = parseInt(selectedYear);
    const categoryTotals = {};
    let totalPending = 0;

    // Create a map of categories
    const catMap = {};
    categories.forEach(cat => {
      catMap[cat.id] = cat;
    });

    bills.forEach(bill => {
      const catId = bill.category_id || "uncategorized";
      const catName = catMap[catId]?.name || "Sem Categoria";
      const catColor = catMap[catId]?.color || "#8A8A8A";

      bill.installment_details?.forEach(inst => {
        if (inst.is_paid) return;
        
        const dueDate = new Date(inst.due_date);
        const instMonth = dueDate.getMonth() + 1;
        const instYear = dueDate.getFullYear();

        if (instMonth === month && instYear === year) {
          if (!categoryTotals[catId]) {
            categoryTotals[catId] = {
              name: catName,
              value: 0,
              color: catColor
            };
          }
          categoryTotals[catId].value += inst.value;
          totalPending += inst.value;
        }
      });
    });

    return {
      data: Object.values(categoryTotals).sort((a, b) => b.value - a.value),
      total: totalPending
    };
  };

  // Calculate paid bills by category for selected month (using paid_at date)
  const getPaidByCategory = () => {
    const month = parseInt(selectedMonth);
    const year = parseInt(selectedYear);
    const categoryTotals = {};
    let totalPaid = 0;

    // Create a map of categories
    const catMap = {};
    categories.forEach(cat => {
      catMap[cat.id] = cat;
    });

    bills.forEach(bill => {
      const catId = bill.category_id || "uncategorized";
      const catName = catMap[catId]?.name || "Sem Categoria";
      const catColor = catMap[catId]?.color || "#8A8A8A";

      bill.installment_details?.forEach(inst => {
        if (!inst.is_paid) return;
        
        // Check paid_at date
        const paidAt = inst.paid_at;
        if (!paidAt) return;
        
        const paidDate = new Date(paidAt);
        const paidMonth = paidDate.getMonth() + 1;
        const paidYear = paidDate.getFullYear();

        if (paidMonth === month && paidYear === year) {
          if (!categoryTotals[catId]) {
            categoryTotals[catId] = {
              name: catName,
              value: 0,
              color: catColor
            };
          }
          categoryTotals[catId].value += inst.value;
          totalPaid += inst.value;
        }
      });
    });

    return {
      data: Object.values(categoryTotals).sort((a, b) => b.value - a.value),
      total: totalPaid
    };
  };

  const pendingData = getPendingByCategory();
  const paidData = getPaidByCategory();

  const getUpcomingBills = () => {
    const today = new Date();
    const upcoming = [];
    
    bills.forEach(bill => {
      bill.installment_details?.forEach(inst => {
        if (!inst.is_paid) {
          const dueDate = new Date(inst.due_date);
          const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
          if (diffDays >= 0 && diffDays <= 7) {
            upcoming.push({
              ...bill,
              installment: inst,
              diffDays
            });
          }
        }
      });
    });
    
    return upcoming.sort((a, b) => new Date(a.installment.due_date) - new Date(b.installment.due_date)).slice(0, 5);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const chartData = stats?.category_totals?.map((cat, idx) => ({
    name: cat.name,
    value: cat.total,
    pending: cat.pending,
    color: cat.color || CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
  })) || [];

  const upcomingBills = getUpcomingBills();

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Visão geral das suas contas</p>
        </div>
        <Link to="/bills">
          <Button className="tracing-beam gap-2" data-testid="add-bill-button">
            <Plus size={18} weight="bold" />
            Nova Conta
          </Button>
        </Link>
      </div>

      {/* Stats Grid - Bento Style */}
      <div className="bento-grid">
        {/* Total Pendente - Clickable */}
        <Card 
          className="bento-item card-hover cursor-pointer" 
          data-testid="stat-pending"
          onClick={() => navigate('/bills')}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground font-medium">Total Pendente</p>
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowPending(!showPending); }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-pending-visibility"
                >
                  {showPending ? <Eye size={16} /> : <EyeSlash size={16} />}
                </button>
              </div>
              <p className="font-heading text-3xl font-bold mt-2 text-foreground">
                {showPending ? formatCurrency(stats?.total_pending) : "R$ •••••"}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <CurrencyDollar size={24} className="text-primary" weight="fill" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Clique para ver todas as contas</p>
        </Card>

        {/* Vencidas - Clickable */}
        <Card 
          className="bento-item card-hover border-destructive/30 cursor-pointer" 
          data-testid="stat-overdue"
          onClick={() => navigate('/bills')}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground font-medium">Vencidas</p>
              </div>
              <p className="font-heading text-3xl font-bold mt-2 text-destructive">
                {showPending ? formatCurrency(stats?.total_overdue) : "R$ •••••"}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center">
              <Warning size={24} className="text-destructive" weight="fill" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Clique para ver contas vencidas</p>
        </Card>

        {/* Vencem Hoje - Clickable */}
        <Card 
          className="bento-item card-hover cursor-pointer" 
          data-testid="stat-today"
          onClick={() => navigate('/bills')}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-muted-foreground font-medium">Vencem Hoje</p>
              <p className="font-heading text-3xl font-bold mt-2 text-foreground">
                {stats?.bills_due_today || 0}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
              <Clock size={24} className="text-yellow-600" weight="fill" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Clique para ver contas de hoje</p>
        </Card>

        {/* Pago este Mês - Clickable */}
        <Card 
          className="bento-item card-hover cursor-pointer" 
          data-testid="stat-paid"
          onClick={() => navigate('/bills?filter=paid')}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground font-medium">Pago este Mês</p>
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowPaid(!showPaid); }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-paid-visibility"
                >
                  {showPaid ? <Eye size={16} /> : <EyeSlash size={16} />}
                </button>
              </div>
              <p className="font-heading text-3xl font-bold mt-2 text-primary">
                {showPaid ? formatCurrency(stats?.total_paid_this_month) : "R$ •••••"}
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
              <CalendarCheck size={24} className="text-green-600" weight="fill" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Clique para ver contas pagas</p>
        </Card>

        {/* Chart - Span 2 columns - Clickable */}
        <Card 
          className="bento-item span-2 row-2 cursor-pointer" 
          data-testid="category-chart"
          onClick={() => navigate('/categories')}
        >
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <ChartPie size={20} className="text-primary" weight="fill" />
              <CardTitle className="font-heading text-lg">Gastos por Categoria</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={50}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {chartData.slice(0, 5).map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="text-sm text-muted-foreground flex-1 truncate">
                        {cat.name}
                      </span>
                      <span className="text-sm font-medium font-mono">
                        {formatCurrency(cat.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                Nenhuma categoria cadastrada
              </div>
            )}
          </CardContent>
        </Card>

        {/* A Pagar por Categoria - New Chart with Month Filter */}
        <Card className="bento-item span-2 row-2" data-testid="pending-category-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Wallet size={20} className="text-amber-600" weight="fill" />
                <CardTitle className="font-heading text-lg">A Pagar por Categoria</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(month => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Total: {formatCurrency(pendingData.total)}
            </p>
          </CardHeader>
          <CardContent>
            {pendingData.data.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie
                      data={pendingData.data}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      innerRadius={45}
                    >
                      {pendingData.data.map((entry, index) => (
                        <Cell key={`cell-pending-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {pendingData.data.slice(0, 5).map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="text-sm text-muted-foreground flex-1 truncate">
                        {cat.name}
                      </span>
                      <span className="text-sm font-medium font-mono">
                        {formatCurrency(cat.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-44 flex items-center justify-center text-muted-foreground">
                Nenhuma conta a pagar em {MONTHS[parseInt(selectedMonth) - 1]?.label}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagas por Categoria - Chart with Month Filter */}
        <Card className="bento-item span-2 row-2" data-testid="paid-category-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <CalendarCheck size={20} className="text-green-600" weight="fill" />
                <CardTitle className="font-heading text-lg">Pagas por Categoria</CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(month => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Total: {formatCurrency(paidData.total)}
            </p>
          </CardHeader>
          <CardContent>
            {paidData.data.length > 0 ? (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={180}>
                  <PieChart>
                    <Pie
                      data={paidData.data}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      innerRadius={45}
                    >
                      {paidData.data.map((entry, index) => (
                        <Cell key={`cell-paid-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {paidData.data.slice(0, 5).map((cat, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: cat.color }}
                      />
                      <span className="text-sm text-muted-foreground flex-1 truncate">
                        {cat.name}
                      </span>
                      <span className="text-sm font-medium font-mono">
                        {formatCurrency(cat.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-44 flex items-center justify-center text-muted-foreground">
                Nenhuma conta paga em {MONTHS[parseInt(selectedMonth) - 1]?.label}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Bills - Span 2 columns, 2 rows */}
        <Card className="bento-item span-2 row-2" data-testid="upcoming-bills">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendUp size={20} className="text-primary" weight="fill" />
                <CardTitle className="font-heading text-lg">Próximos Vencimentos</CardTitle>
              </div>
              <Link to="/bills" className="text-sm text-primary hover:underline flex items-center gap-1">
                Ver todas <ArrowRight size={14} />
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {upcomingBills.length > 0 ? (
              <div className="space-y-3">
                {upcomingBills.map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-4 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors stagger-item"
                  >
                    <div 
                      className="w-2 h-10 rounded-full"
                      style={{ backgroundColor: item.category_color || '#8A8A8A' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{item.description}</p>
                      <p className="text-xs text-muted-foreground">
                        Parcela {item.installment.installment_number}/{item.installments}
                        {item.category_name && ` • ${item.category_name}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-medium text-sm">
                        {formatCurrency(item.installment.value)}
                      </p>
                      <p className={`text-xs ${
                        item.diffDays === 0 ? 'text-yellow-600 font-medium' : 'text-muted-foreground'
                      }`}>
                        {item.diffDays === 0 ? 'Hoje' : 
                         item.diffDays === 1 ? 'Amanhã' : 
                         `Em ${item.diffDays} dias`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-48 flex items-center justify-center text-muted-foreground">
                Nenhum vencimento próximo
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
